import { useRef, useEffect, useState, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import type {
  FieldErrors,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslation } from '@/utils/i18n/useTranslation';

import FormError from '@/components/FormError/FormError';
import Svg from '@/components/Svg/Svg';
import { UserContext } from '@/context/UserContext';
import type {
  FormItemComponentProps,
  FormItemRequiredState,
} from '@/types/domain';

// `errors` is read off the context but RHF exposes it on `formState.errors`; see FormItemTextfield.
type FormContextWithErrors = UseFormReturn & { errors?: FieldErrors };

const revealClass = ' is-revealed';
const activeClass = ' is-active';

const createMarkup = (copy?: string) =>
  ({ __html: copy }) as { __html: string };

const FormItemSelect = (props: FormItemComponentProps) => {
  const {
    item,
    onFormItemChange,
    onFormItemLoad,
    groupedState,
    revealState,
    shouldValidateState,
  } = props;

  const { register, watch, errors, formState, trigger } =
    useFormContext() as unknown as FormContextWithErrors;

  const userContext = useContext(UserContext);

  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED');

  const [isRequired, setIsRequired] = useState<FormItemRequiredState>({
    required: false,
  });
  const [isGrouped, setIsGrouped] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const itemRef = useRef<HTMLDivElement>(null);
  const itemProps = item;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemValue = watch(item.name!) as string;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemInputRef = register(item.name!, isRequired as RegisterOptions);

  useEffect(() => {
    if (!item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: requiredErrorMessage });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, []);

  useEffect(() => {
    if (!itemValue) return;
    if (isLoaded) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      onFormItemChange!({ itemProps, itemRef, itemValue });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      onFormItemLoad!({ itemProps, itemRef, itemValue });
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [itemValue]);

  useEffect(() => {
    if (!item.revealed || !item.required) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    setIsRequired({ required: revealState ? requiredErrorMessage : false });
    void trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [revealState]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    if (!shouldValidateState) setIsRequired({ required: false });
  }, [shouldValidateState]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!(item?.group && groupedState)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    const itemLabel = item?.group?.label;
    const groupedItem = groupedState.find((str) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return str.label.includes(itemLabel!);
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, react-hooks/set-state-in-effect -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setIsGrouped(groupedItem!.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [groupedState]);

  /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${String(item.type)}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        revealState ? revealClass : ''
      }${isGrouped ? activeClass : ''}`}
      data-reveal-target={item.revealed}
    >
      {item.radios!.length > 0 &&
        item.radios!.map((radio, index) => {
          return (
            <div
              className="FormItem-radioButton"
              key={`${String(item.name)}_${String(radio.id)}_${String(index)}`}
            >
              <label
                className="FormItem-radioButtonLabel"
                htmlFor={`${String(item.name)}_${String(radio.id)}`}
              >
                <input
                  type="radio"
                  name={item.name}
                  id={`${String(item.name)}_${String(radio.id)}`}
                  className={`FormItem-input FormItem-input--${String(item.type)}`}
                  value={radio.value}
                  defaultChecked={
                    (item.defaultChecked === radio.value
                      ? item.defaultChecked
                      : '') as unknown as boolean
                  }
                  onChange={(e) => {
                    void itemInputRef.onChange(e);
                  }}
                  ref={itemInputRef.ref}
                />
                <div className="FormItem-radioButtonIndicator">
                  <Svg
                    id="icon-check"
                    modifiers={['FormItem-radioButtonIcon']}
                  />
                </div>
                <span
                  className="FormItem-radioButtonCopy"
                  dangerouslySetInnerHTML={createMarkup(
                    radio.transl ? t(radio.transl) : radio.label,
                  )}
                ></span>
              </label>
            </div>
          );
        })}
      {formState.isSubmitted && (
        <ErrorMessage errors={errors} name={item.name!} as={<FormError />} />
      )}
    </div>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
};

export default FormItemSelect;
